import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ListSubheader, MenuItem, TextField } from '@mui/material';
import type { FloorPlanRoomOption } from '../models/floorplan-room';
import { groupRoomsByFloor } from '../utils/room-groups';

interface Props {
  readonly rooms: FloorPlanRoomOption[];
  readonly storey: number;
  readonly value: string;
  readonly onChange: (roomId: string) => void;
  /** Rooms used by desks already on this storey, including ones placed but not yet saved. */
  readonly roomIdsOnThisFloor?: ReadonlySet<string>;
  readonly helperText?: string;
  readonly autoFocus?: boolean;
  /**
   * Set when the rooms could not be read at all, as opposed to having been read and found to
   * be none. Only the second is the admin's to fix by adding a room, so only the second may
   * say so.
   */
  readonly unavailable?: boolean;
}

/**
 * Picks the room a desk belongs to, with this floor's rooms first.
 *
 * Shared by the dialog that appears when a desk is placed and by the properties panel, so
 * that changing a desk's room afterwards offers exactly the same choice, grouped the same
 * way.
 *
 * Rooms belonging to another floor are not offered at all. The one exception is the room the
 * selected desk is already in, which `groupRoomsByFloor` keeps in the first group whatever
 * floor it is on — a select whose own value is missing renders blank, and the next click on
 * it would silently reassign the desk.
 */
const RoomSelectField = ({
  rooms,
  storey,
  value,
  onChange,
  roomIdsOnThisFloor,
  helperText,
  autoFocus,
  unavailable = false,
}: Props): React.JSX.Element => {
  const { t } = useTranslation();

  const grouped = useMemo(
    () => groupRoomsByFloor({ rooms, storey, roomIdsOnThisFloor, currentRoomId: value || null }),
    [rooms, storey, roomIdsOnThisFloor, value],
  );

  const label = (room: FloorPlanRoomOption): string => `${room.name} (${room.workplaceCount}/${room.capacity})`;

  /**
   * An empty list means one of two things, and only one of them is the admin's to act on:
   * the office has no rooms yet, or the rooms could not be read at all.
   */
  const helperTextFor = (): string | undefined => {
    if (unavailable) {
      return String(t('floorplan-editor-rooms-unavailable-short'));
    }

    if (rooms.length === 0) {
      return String(t('floorplan-editor-room-picker-no-rooms'));
    }

    return helperText;
  };

  // Built as one flat array because a MUI Select reads its children directly: a group wrapped
  // in a fragment or a <div> stops the items being found, and the value renders blank.
  const options: React.JSX.Element[] = [];

  if (grouped.thisFloor.length > 0) {
    options.push(
      <ListSubheader key="header-this-floor">{t('floorplan-editor-rooms-on-this-floor')}</ListSubheader>,
    );
    grouped.thisFloor.forEach((room) => options.push(
      <MenuItem key={room.id} value={room.id}>{label(room)}</MenuItem>,
    ));
  }

  if (grouped.unplaced.length > 0) {
    options.push(
      <ListSubheader key="header-unplaced">{t('floorplan-editor-rooms-not-on-a-floor')}</ListSubheader>,
    );
    grouped.unplaced.forEach((room) => options.push(
      <MenuItem key={room.id} value={room.id}>{label(room)}</MenuItem>,
    ));
  }

  return (
    <TextField
      select
      size="small"
      autoFocus={autoFocus}
      label={String(t('floorplan-editor-room'))}
      value={value}
      onChange={(event): void => onChange(event.target.value)}
      helperText={helperTextFor()}
      disabled={rooms.length === 0}
      inputProps={{ 'aria-label': String(t('floorplan-editor-room')) }}
    >
      {options}
    </TextField>
  );
};

export default RoomSelectField;
